import { VCPM } from '../models/project.model';
import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';
import { VcfSizingOutput } from '../models/vcf-sizing-output.model';
export { isWorkloadStretchedByInput } from './workload-stretch.transform';
import { partitionProfileRecommendationsBySide } from '../adapters/sizing-recalculation.adapter';

/**
 * AS-IS relocation (plan §1.5) - each function mirrors ONE existing call-site helper 1:1.
 * No unification: the 4 detection paths / 3 multiplier strategies across the 5 output
 * components disagree in practice and are deliberately NOT reconciled here.
 *
 * Model gap (not fixed): plan §2.4 types these against `VcfSizingOutput.Cluster[]` /
 * `PcsSizingEngineResponse.Component[]`, but every real call site (management-domain,
 * workload-domain, std-gf-recommendation components) actually operates on
 * `PcsSizingEngineResponse.Cluster[]` / `PcsSizingEngineResponse.SoftwareComponent[]` - there is
 * no `Cluster` type in the `VcfSizingOutput` namespace. Typed against the real shapes instead.
 */

/**
 * Ported as-is from WorkloadDomainComponent.isWorkloadStretched/isStretchedClusterWorkword
 * (workload-domain.component.ts:808-818, 974-994) - the input-side stretched check performed
 * against the original workload's clusterConfiguration, as opposed to isStretchedByRecommendation
 * which checks the output recommendation's cluster siteTypes.
 */
export function isStretchedByInput(clusterConfiguration: VCPM.ClusterConfiguration | undefined): boolean {
  return clusterConfiguration?.clusterType === 'stretched';
}

/**
 * Ported as-is from ManagementDomainComponent.isStretchedCluster (management-domain.component.ts:656-659)
 * and the equivalent inline checks in StdGfRecommendationComponent.
 */
export function isStretchedByRecommendation(clusters: PcsSizingEngineResponse.Cluster[] | undefined): boolean {
  return clusters?.some((c) => c.siteType === 'PRIMARY' || c.siteType === 'SECONDARY' || c.siteType === 'WITNESS') ?? false;
}

/**
 * Ported as-is from the repeated `isStretched ? 2 : 1` / `clusterType === 'stretched' ? 2 : 1`
 * literal pattern across all 5 output components.
 */
export function getFlatStretchedMultiplier(isStretched: boolean): 1 | 2 {
  return isStretched ? 2 : 1;
}

/**
 * Ported as-is from WorkloadDomainComponent.getStretchedMultiplierForComponent
 * (workload-domain.component.ts:790-803). `key` is derived by the caller
 * (`${workloadId}-${productName}-${version}`) - this function only does the
 * filter-and-count-with-floor-of-1 that was previously inline.
 */
export function getDuplicateRowMultiplier(components: PcsSizingEngineResponse.SoftwareComponent[] | undefined, key: string): number {
  if (!components) {
    return 1;
  }
  const count = components.filter(
    (comp) => comp.enabled && comp.placementType === 'WORKLOAD' && `${comp.workloadId}-${comp.productName}-${comp.version}` === key,
  ).length;
  return Math.max(count, 1);
}

/**
 * Ported as-is from the repeated `isStretched ? clusters.filter(PRIMARY) : clusters.filter(!WITNESS)`
 * pattern (management-domain.component.ts:119, std-gf-recommendation.component.ts:144-146/242-244).
 */
export function filterPrimarySiteClusters(clusters: PcsSizingEngineResponse.Cluster[], isStretched: boolean): PcsSizingEngineResponse.Cluster[] {
  // A cluster with no siteType (e.g. the CON/HCI side of a dHCI workload where only the storage
  // cluster is stretched) is single-site, so it belongs to the "primary" set rather than being dropped.
  return isStretched ? clusters.filter((c) => c.siteType === 'PRIMARY' || !c.siteType) : clusters.filter((c) => c.siteType !== 'WITNESS');
}

/**
 * Ported as-is from WorkloadDomainComponent.getTotalWitnessClusters
 * (workload-domain.component.ts:1021-1028) and the equivalent inline witness-count loops in
 * StdGfRecommendationComponent.
 */
export function countWitnessClusters(clusters: PcsSizingEngineResponse.Cluster[] | undefined): number {
  return clusters?.filter((c) => c.siteType === 'WITNESS' && c.clusterType !== 'CON').length ?? 0;
}

function convertResourceValuePerUnit(resource: PcsSizingEngineResponse.StrValueUnit, unit: string): string {
  if (unit === resource.units) return resource.value;
  if (unit === 'TB' && resource.units === 'GB') return (parseFloat(resource.value) / 1000).toFixed(2).toString();
  if (unit === 'GB' && resource.units === 'TB') return (parseFloat(resource.value) * 1000).toFixed(2).toString();
  return resource.value;
}

/** Cluster display name: the cluster's own `clusterName`, with `clusterSplitName` appended to
 *  distinguish threshold-triggered split parts that share one `clusterName` (`{clusterName}-
 *  {clusterSplitName}`). Falls back to whichever of the two is present when only one is set. */
export function stripClusterSiteSuffix(name: string): string {
  return name.replace(/\s*-\s*(?:primary|secondary)\s+site\s*$/i, '').trim();
}

export function buildClusterDisplayName(c: PcsSizingEngineResponse.Cluster): string {
  const name = stripClusterSiteSuffix(c.clusterName ?? '');
  const split = stripClusterSiteSuffix(c.clusterSplitName ?? '');
  if (name && split && split !== name) return `${name} ${split}`;
  return name || split;
}

/**
 * Ported as-is from StdGfRecommendationComponent.buildClusterSummaries
 * (std-gf-recommendation.component.ts) - builds the per-cluster breakdown cards shown in the
 * cluster-card carousel. `getVsanArchType` is injected rather than duplicated here since it's
 * Angular-service-owned (PCSSizerSerive.vsanArchType) but otherwise stateless - callers already
 * have that service injected and just pass it through.
 */
export function buildClusterSummaries(
  w: PcsSizingEngineResponse.WorkloadRecommendation,
  clustersToCount: PcsSizingEngineResponse.Cluster[],
  getVsanArchType: (architecture: string | null | undefined) => string,
  isStretched = false,
  sharingContext: ClusterSharingContext = {},
  defaultClusters?: PcsSizingEngineResponse.Cluster[],
): VcfSizingOutput.ClusterSummary[] {
  return clustersToCount.map((c, idx) => {
    const clusterHosts = parseInt(c.clusterConfiguration.nodesSize.value);
    const clusterMemUnit = c.serverConfiguration.memory.units == 'TB' ? 'TB' : 'GB';
    const clusterMemoryTotal = parseFloat(convertResourceValuePerUnit(c.serverConfiguration.memory, clusterMemUnit)) * clusterHosts;
    const clusterStorageUnit = c.clusterConfiguration.rawCapacity?.units == 'TB' ? 'TB' : 'GB';
    const driveSizeSpec = c.diskGroupConfiguration?.capacitySpecification?.selectedCapacityDriveSize
      ?? c.diskConfiguration?.capacitySpecification?.selectedCapacityDriveSize;
    const clusterRole = clusterRoleFromClusterType(c.clusterType);
    const name = buildClusterDisplayName(c);
    const summary: VcfSizingOutput.ClusterSummary = {
      id: `${w.workloadId}-${idx}`,
      index: idx,
      name,
      clusterId: c.clusterId ?? undefined,
      hosts: clusterHosts,
      vms: parseInt(c.clusterConfiguration.vmsSize.value),
      cores: c.serverConfiguration.cores.value * clusterHosts,
      memory: { value: parseFloat(clusterMemoryTotal.toFixed(2)), unit: clusterMemUnit },
      rawStorage: { value: c.clusterType === 'CON' ? 0 : parseFloat((c.clusterConfiguration.rawCapacity?.value || 0).toString()), unit: clusterStorageUnit },
      memoryUsedByAppsPerHost: { value: parseFloat(c.serverConfiguration.requiredMemory?.value ?? '0'), unit: c.serverConfiguration.requiredMemory?.units ?? 'GB' },
      capacityDrivesPerHost: c.serverConfiguration.capacityDrive?.value ?? 0,
      driveSize: { value: parseFloat(driveSizeSpec?.value ?? '0'), unit: driveSizeSpec?.units ?? 'GB' },
      vsanArchitecture: getVsanArchType(c.vsanArchitecture),
      clusterType: c.clusterType,
      clusterBaseName: c.clusterName ?? undefined,
      // Per cluster, not per workload: an unsited cluster (HCI/CON beside a stretched SON) is single-site, no "x2"/Stretched.
      isStretched: isStretched && !!c.siteType,
      clusterRole,
      ...buildClusterSharingFields(resolveClusterSharingSource(c, defaultClusters), clusterRole, sharingContext),
    } as VcfSizingOutput.ClusterSummary;
    if (c.serverConfiguration.diskGroups) {
      summary.diskGroupsCount = parseFloat(c.serverConfiguration.diskGroups.value) || 0;
    }
    const cacheDriveSize = c.diskGroupConfiguration?.cacheSpecification?.selectedCacheDriveSize;
    if (cacheDriveSize) {
      summary.cacheTierDriveSize = { value: parseFloat(cacheDriveSize.value) || 0, unit: cacheDriveSize.units || 'GB' };
    }
    return summary;
  });
}

/** Flattens every domain's `workloadRecommendations[]` into a workloadId -> display-name map (also
 *  registered under the domain-scoped key `<requirementsId>:<workloadId>`). */
export function buildWorkloadNameById(recommendations: PcsSizingEngineResponse.Recommendation[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  recommendations?.forEach((r) => r.workloadRecommendations?.forEach((wr) => {
    if (!wr.workloadId) return;
    map.set(wr.workloadId, wr.name);
    if (r.requirementsId) map.set(`${r.requirementsId}:${wr.workloadId}`, wr.name);
  }));
  return map;
}

/** CON = Compute-Only Node, SON = Storage-Only Node (dHCI split-cluster roles); plain HCI clusters
 *  have no role and never show the Compute/Storage/Cross-WLD badges. */
export function clusterRoleFromClusterType(clusterType: PcsSizingEngineResponse.Cluster['clusterType']): 'COMPUTE' | 'STORAGE' | undefined {
  if (clusterType === 'CON') return 'COMPUTE';
  if (clusterType === 'SON') return 'STORAGE';
  return undefined;
}

/** Resolution context threaded through buildClusterSharingFields(). `currentDomainId` is the
 *  requirementsId of the domain being rendered; connected items from any other workload domain are
 *  qualified as "DomainName / Name". Omit it to show bare names. */
export interface ClusterSharingContext {
  currentDomainId?: string;
  workloadVmCounts?: ReadonlyMap<string, number>;
}

/** Domain-scoped identity prevents workloads with reused IDs from borrowing another count. */
export function buildWorkloadVmCountLookup(domains: VCPM.WorkloadDomain[] | undefined): Map<string, number> {
  const counts = new Map<string, number>();
  for (const domain of domains ?? []) {
    for (const workload of domain.workloadList ?? []) {
      const count = workload.workloadDetails?.totalCountOfVms;
      if (domain.id != null && workload.id != null && Number.isInteger(count) && count >= 0) {
        counts.set(JSON.stringify([String(domain.id), String(workload.id)]), count);
      }
    }
  }
  return counts;
}

export function buildConnectedWorkloadSummaries(
  workloads: PcsSizingEngineResponse.ConnectedWorkload[] | undefined,
  context: ClusterSharingContext,
): VcfSizingOutput.ConnectedWorkloadSummary[] | undefined {
  if (!workloads?.length) return undefined;
  const seen = new Set<string>();
  return workloads.flatMap(workload => {
    const domainId = String(workload.workloadDomainId);
    const workloadId = String(workload.id);
    const key = JSON.stringify([domainId, workloadId]);
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ domainId, workloadId,
      displayName: context.currentDomainId === undefined || domainId === String(context.currentDomainId)
        ? workload.name : `${workload.name} / ${workload.workloadDomainName}`,
      vmCount: context.workloadVmCounts?.get(key),
    }];
  });
}

/** Cluster View groups every cluster owned across a domain's workloads by its role, so a
 *  threshold-triggered split (multiple entries sharing one clusterType) lands together in a
 *  single group, while HCI/CON/SON assignment (an input-time decision, unrelated to splitting)
 *  determines which group. */
export interface ClusterTypeGroups {
  hci: VcfSizingOutput.ClusterSummary[];
  compute: VcfSizingOutput.ClusterSummary[];
  storage: VcfSizingOutput.ClusterSummary[];
}

export type ClusterGroupKey = 'hci' | 'compute' | 'storage';

/** Single source of truth for the group's plural display label - shared by the tree-nav, the
 *  Cluster View page title, and the breadcrumb, so all three can never drift apart. */
export const CLUSTER_GROUP_TITLES: Record<ClusterGroupKey, string> = {
  hci: 'HCI Clusters',
  compute: 'Compute Clusters',
  storage: 'Storage Clusters',
};

/** Singular counterpart of CLUSTER_GROUP_TITLES, for a single focused cluster's "Cluster Type" label. */
export const CLUSTER_GROUP_SINGULAR_LABELS: Record<ClusterGroupKey, string> = {
  hci: 'HCI Cluster',
  compute: 'Compute Cluster',
  storage: 'Storage Cluster',
};

/** Single source of truth for the display order of cluster-type groups (HCI, then Compute, then
 *  Storage) - shared by every page that renders one section per non-empty group, so they can
 *  never drift apart. */
export const CLUSTER_GROUP_ORDER: ClusterGroupKey[] = ['hci', 'compute', 'storage'];

function buildClusterTypeFallbackName(clusterType: PcsSizingEngineResponse.Cluster['clusterType'], position: number): string {
  const role = clusterRoleFromClusterType(clusterType);
  const group: ClusterGroupKey = role === 'COMPUTE' ? 'compute' : role === 'STORAGE' ? 'storage' : 'hci';
  return `${CLUSTER_GROUP_SINGULAR_LABELS[group]} ${position + 1}`;
}

/** Human-readable fallback for a ClusterSummary with no clusterName (older saved projects, from
 *  before the engine persisted clusterName on its output) - "HCI Cluster 2" style - so cluster
 *  cards and detail headings never render a blank name. */
export function resolveClusterDisplayName(cluster: Pick<VcfSizingOutput.ClusterSummary, 'name' | 'clusterType' | 'index'>): string {
  return stripClusterSiteSuffix(cluster.name) || buildClusterTypeFallbackName(cluster.clusterType, cluster.index);
}

/** Where one cluster card sits in BOTH coordinate systems Cluster View uses: the flat type-group
 *  array the tree nav indexes leaves by (`group`/`indexInGroup`), and the workload that owns it
 *  (`workloadIndex` - position within `workloads`, since `VcfSizingOutput.Workload` carries no id
 *  of its own), which is what the full-detail page (WorkloadDomainComponent) is scoped to. */
export interface DomainClusterLocation {
  group: ClusterGroupKey;
  indexInGroup: number;
  // -1 for a domain-level SON; its sourceRecommendation identifies the real backing workload.
  workloadIndex: number;
  cluster: VcfSizingOutput.ClusterSummary;
}

/** Single source of truth for Cluster View's leaf ordering - groupDomainClustersByType() below is
 *  reimplemented on top of this so the tree's per-group leaf index and indexInGroup can never
 *  drift apart. Iterates in workload order, then each workload's own cluster order (unchanged from
 *  groupDomainClustersByType's original loop), CON -> compute, SON -> storage, else -> hci.
 *  Dedupes by clusterId (first occurrence wins) AND by clusterName across workloads - a cluster shared
 *  by multiple workloads in the same domain (dHCI CON/SON split, or multi-workload-in-a-cluster) shows
 *  up once per owning workload's own `clusters` array, and would otherwise render as a duplicate tree
 *  leaf/card. The engine mints clusterIds per call, so one shared cluster can carry different ids in
 *  two workloads (e.g. after a Recalculate); the name is what identifies it. A name repeated inside the
 *  FIRST workload's own list is a threshold split and is kept. Clusters with neither are never deduped. */
export function buildDomainClusterLocations(workloads: VcfSizingOutput.Workload[] | VcfSizingOutput.WorkloadDomain | undefined): DomainClusterLocation[] {
  const counters: Record<ClusterGroupKey, number> = { hci: 0, compute: 0, storage: 0 };
  const seenClusterIds = new Set<string>();
  // clusterName -> index of the first workload that listed it. Response clusterIds are minted per engine
  // call (a Recalculate result and the original tier carry different ones for the same cluster), so the
  // same name from a LATER workload is the same shared cluster, not another split of it. Splits of one
  // cluster share a clusterName inside ONE workload's list, so every cluster of the first workload stays.
  const firstWorkloadByName = new Map<string, number>();
  const locations: DomainClusterLocation[] = [];
  const domain = Array.isArray(workloads) ? undefined : workloads;
  const actualWorkloads = Array.isArray(workloads) ? workloads : domain?.workloads;
  const visit = (cluster: VcfSizingOutput.ClusterSummary, workloadIndex: number) => {
    const name = clusterIdentityKey(cluster);
    if (name) {
      const firstWorkload = firstWorkloadByName.get(name);
      if (firstWorkload !== undefined && firstWorkload !== workloadIndex) return;
      firstWorkloadByName.set(name, workloadIndex);
    }
    if (cluster.clusterId) {
      if (seenClusterIds.has(cluster.clusterId)) return;
      seenClusterIds.add(cluster.clusterId);
    }
    const group: ClusterGroupKey = cluster.clusterType === 'CON' ? 'compute' : cluster.clusterType === 'SON' ? 'storage' : 'hci';
    locations.push({ group, indexInGroup: counters[group]++, workloadIndex, cluster });
  };
  actualWorkloads?.forEach((w, workloadIndex) => w.clusters?.forEach(c => visit(c, workloadIndex)));
  domain?.ownedStorageClusters?.forEach(c => visit(c, -1));
  return locations;
}

/** The key two workloads' copies of one shared cluster agree on: its cluster type + `clusterName` (the UI's
 *  unique cluster name, echoed by the engine). The response `clusterId` is NOT usable for this - the engine
 *  mints it per call. Empty for older data without a name, which callers then leave to the clusterId rule.
 *  One place to change once the engine echoes the real cluster id in chassis.id. */
export function clusterIdentityKey(cluster: { clusterName?: string | null; clusterBaseName?: string | null; clusterType?: string | null }): string {
  const name = cluster.clusterBaseName || cluster.clusterName;
  return name ? `${cluster.clusterType ?? ''}::${name}` : '';
}

export function groupDomainClustersByType(workloads: VcfSizingOutput.Workload[] | VcfSizingOutput.WorkloadDomain | undefined): ClusterTypeGroups {
  const groups: ClusterTypeGroups = { hci: [], compute: [], storage: [] };
  buildDomainClusterLocations(workloads).forEach(loc => groups[loc.group].push(loc.cluster));
  return groups;
}

/** Cluster View's tree leaf (`wld-{domain}-{group}-{indexInGroup}`) resolves directly to this. */
export function findClusterLocationAt(
  workloads: VcfSizingOutput.Workload[] | VcfSizingOutput.WorkloadDomain | undefined,
  group: ClusterGroupKey,
  indexInGroup: number,
): DomainClusterLocation | undefined {
  return buildDomainClusterLocations(workloads).find(loc => loc.group === group && loc.indexInGroup === indexInGroup);
}

/** A bare group node (`wld-{domain}-{group}`, no leaf chosen) behaves like its first leaf. */
export function findFirstClusterLocationInGroup(
  workloads: VcfSizingOutput.Workload[] | VcfSizingOutput.WorkloadDomain | undefined,
  group: ClusterGroupKey,
): DomainClusterLocation | undefined {
  return buildDomainClusterLocations(workloads).find(loc => loc.group === group);
}

/** First HCI cluster in domain order; for domains with no HCI cluster (pure dHCI), falls back to
 *  the first CON/compute cluster. Returns the ClusterSummary.id that should host the domain's
 *  optional workload-domain components (NSX Edge, HCX, etc.) - the same cluster the sizing engine
 *  adds their extra infra capacity to. A split cluster's first split is picked automatically since
 *  this returns the first matching location in domain order, not an aggregate. */
export function resolveComponentsTargetClusterId(workloads: VcfSizingOutput.Workload[] | VcfSizingOutput.WorkloadDomain | undefined): string | undefined {
  return (findFirstClusterLocationInGroup(workloads, 'hci') ?? findFirstClusterLocationInGroup(workloads, 'compute'))?.cluster.id;
}

/** One tree-nav leaf after collapsing a threshold-triggered cluster split ("Cluster 1", "Cluster
 *  2", ... sharing one clusterBaseName) into a single entry. `location` is the FIRST split
 *  encountered - deliberately reusing its own real, unchanged `indexInGroup`/node-id so every
 *  existing index-based resolver (findClusterLocationAt, getActiveClusterIndex, etc.) keeps
 *  working unmodified; only the tree's rendering (label + which leaves it iterates) needs to
 *  know about collapsing. */
export interface ClusterSplitGroupEntry {
  location: DomainClusterLocation;
  baseName: string;
  splitCount: number;
}

/** Grouping key shared by buildCollapsedClusterLocations() and resolveCollapsedClusterLocation(). */
function clusterSplitGroupKey(loc: DomainClusterLocation): string {
  // No shared identity to group real splits by when both are empty (older saved data, from
  // before the engine persisted clusterName) - key on clusterId, or workloadIndex+indexInGroup
  // as a last resort, so distinct unnamed clusters never collapse into one leaf the way two
  // empty strings would.
  const dedupKey = loc.cluster.clusterBaseName || loc.cluster.name || loc.cluster.clusterId || `${loc.workloadIndex}-${loc.indexInGroup}`;
  return `${loc.group}::${dedupKey}`;
}

/** Groups buildDomainClusterLocations()'s flat per-split leaves by (group, clusterBaseName),
 *  preserving first-occurrence order within each group. A cluster with no clusterBaseName (older/
 *  incomplete data) is treated as its own singleton group, keyed by its own display name, so it
 *  still renders as exactly one leaf. */
export function buildCollapsedClusterLocations(workloads: VcfSizingOutput.Workload[] | VcfSizingOutput.WorkloadDomain | undefined): Record<ClusterGroupKey, ClusterSplitGroupEntry[]> {
  const result: Record<ClusterGroupKey, ClusterSplitGroupEntry[]> = { hci: [], compute: [], storage: [] };
  const entryByKey = new Map<string, ClusterSplitGroupEntry>();
  buildDomainClusterLocations(workloads).forEach(loc => {
    const rawBaseName = loc.cluster.clusterBaseName || loc.cluster.name;
    const key = clusterSplitGroupKey(loc);
    const existing = entryByKey.get(key);
    if (existing) {
      existing.splitCount++;
      return;
    }
    const baseName = rawBaseName || buildClusterTypeFallbackName(loc.cluster.clusterType, loc.indexInGroup);
    const entry: ClusterSplitGroupEntry = { location: loc, baseName, splitCount: 1 };
    entryByKey.set(key, entry);
    result[loc.group].push(entry);
  });
  return result;
}

/** Domain-level Hosts/VMs/Cores/Memory/Raw-storage totals, matching the engine's own
 *  `domainSummary.numberOfHosts`/`numberOfVMs` accounting. Operates on each workload's effective raw
 *  clusters (so it follows a live Recalculate/tier change). Rules:
 *  - every non-WITNESS cluster counts once - a stretched cluster's PRIMARY and SECONDARY sites are
 *    separate entries that each carry their own values, so no x2 multiplier is applied here;
 *  - a cluster shared by several workloads of the domain (same clusterIdentityKey) counts once, via the
 *    first workload that lists it; repeated names inside that first workload are threshold splits and
 *    all count;
 *  - a cluster owned by another domain (`ownerRequirementsId` differs from `domainId`) is excluded;
 *  - VMs skip SON clusters: a storage-only cluster's vmsSize repeats the VMs already counted on the
 *    compute/HCI clusters that mount it (its hosts are physical and still count).
 *  Memory and storage are returned in TB. Usable/used-by-workloads storage are filled only when a
 *  `getStorageUsageBreakdown` is supplied (ClusterUsageCalculationService's, injected as in project-summary.adapter). */
export function aggregateDomainInfra(
  workloadClusters: PcsSizingEngineResponse.Cluster[][],
  domainId: string | undefined,
  getStorageUsageBreakdown?: (clusters: PcsSizingEngineResponse.Cluster[]) => { usable: number; usedByWorkloads: number; unit: string } | undefined,
): VcfSizingOutput.InfraSummaryNumberValues {
  const result = new VcfSizingOutput.InfraSummaryNumberValues();
  const firstWorkloadByKey = new Map<string, number>();
  let memoryGb = 0;
  let storageGb = 0;
  const counted: PcsSizingEngineResponse.Cluster[] = [];
  workloadClusters.forEach((clusters, workloadIndex) => (clusters ?? []).forEach((c) => {
    if (c.siteType === 'WITNESS') return;
    if (c.ownerRequirementsId && domainId != null && String(c.ownerRequirementsId) !== String(domainId)) return;
    const key = clusterIdentityKey(c);
    if (key) {
      const first = firstWorkloadByKey.get(key);
      if (first !== undefined && first !== workloadIndex) return;
      firstWorkloadByKey.set(key, workloadIndex);
    }
    counted.push(c);
    const hosts = parseFloat(c.clusterConfiguration.nodesSize.value) || 0;
    result.hosts.value += hosts;
    if (c.clusterType !== 'SON') {
      result.vms.value += parseFloat(c.clusterConfiguration.vmsSize.value) || 0;
    }
    result.cores.value += (c.serverConfiguration.cores.value || 0) * hosts;
    memoryGb += (parseFloat(convertResourceValuePerUnit(c.serverConfiguration.memory, 'GB')) || 0) * hosts;
    if (c.clusterType !== 'CON') {
      const raw = c.clusterConfiguration.rawCapacity;
      storageGb += (parseFloat(String(raw?.value ?? 0)) || 0) * (raw?.units === 'TB' ? 1000 : 1);
    }
  }));
  result.memory = { value: parseFloat((memoryGb / 1000).toFixed(2)), unit: 'TB' };
  result.rawStorage = { value: parseFloat((storageGb / 1000).toFixed(2)), unit: 'TB' };
  const storageClusters = counted.filter(c => c.clusterType !== 'CON');
  result.hasStorage = storageClusters.length > 0;
  if (getStorageUsageBreakdown) {
    // Usable (raw - overheads) / used-by-workloads over the SAME deduped, owned cluster set as the raw
    // storage above. Broken down per vSAN architecture because the breakdown picks its ESA-vs-OSA
    // formula from the first cluster it is handed, so a mixed domain must not be passed in one go.
    const byArch = new Map<string, PcsSizingEngineResponse.Cluster[]>();
    storageClusters.forEach((c) => byArch.set(c.vsanArchitecture ?? '', [...(byArch.get(c.vsanArchitecture ?? '') ?? []), c]));
    let usableGb = 0;
    let usedGb = 0;
    byArch.forEach((clusters) => {
      const b = getStorageUsageBreakdown(clusters);
      if (!b) return;
      const toGb = b.unit === 'TB' ? 1000 : 1;
      usableGb += b.usable * toGb;
      usedGb += b.usedByWorkloads * toGb;
    });
    result.usableStorage = { value: parseFloat((usableGb / 1000).toFixed(2)), unit: 'TB' };
    result.usedByWorkloadsStorage = { value: parseFloat((usedGb / 1000).toFixed(2)), unit: 'TB' };
  }
  return result;
}

/** Inverse direction: a rendered card -> its tree position, for the Details button. Tiered so it
 *  survives frozen-summary-vs-live-recommendation drift (a live Recalculate can change a
 *  workload's cluster set before `summary` is rebuilt): 1) clusterId (both card builders populate
 *  it), 2) name (buildClusterDisplayName is identical in both builders), 3) positional
 *  (workloadIndexHint + card.index) - only usable when the caller already knows which workload
 *  rendered the card. Returns undefined (a deliberate no-op for callers) rather than throwing when
 *  nothing matches. */
export function resolveClusterLocation(
  workloads: VcfSizingOutput.Workload[] | VcfSizingOutput.WorkloadDomain | undefined,
  card: Pick<VcfSizingOutput.ClusterSummary, 'clusterId' | 'name' | 'index'> | undefined,
  workloadIndexHint?: number,
): DomainClusterLocation | undefined {
  if (!card) return undefined;
  const locations = buildDomainClusterLocations(workloads);
  if (card.clusterId) {
    const byId = locations.find(loc => loc.cluster.clusterId === card.clusterId);
    if (byId) return byId;
  }
  if (card.name) {
    const byName = locations.find(loc => loc.cluster.name === card.name);
    if (byName) return byName;
  }
  if (workloadIndexHint != null) {
    return locations.find(loc => loc.workloadIndex === workloadIndexHint && loc.cluster.index === card.index);
  }
  return undefined;
}

/** Details-button variant of resolveClusterLocation(): a threshold-split cluster's card resolves to
 *  the aggregated (collapsed) tree leaf - the first split's location - rather than its own split's
 *  leaf, so navigation lands on the same node the tree shows for the whole split group. */
export function resolveCollapsedClusterLocation(
  workloads: VcfSizingOutput.Workload[] | VcfSizingOutput.WorkloadDomain | undefined,
  card: Pick<VcfSizingOutput.ClusterSummary, 'clusterId' | 'name' | 'index'> | undefined,
  workloadIndexHint?: number,
): DomainClusterLocation | undefined {
  const loc = resolveClusterLocation(workloads, card, workloadIndexHint);
  if (!loc) return undefined;
  const key = clusterSplitGroupKey(loc);
  const entry = buildCollapsedClusterLocations(workloads)[loc.group].find(e => clusterSplitGroupKey(e.location) === key);
  return entry?.location ?? loc;
}

/** One workload's own (already primary-site-filtered) raw clusters paired 1:1 with the
 *  ClusterSummary cards already built for them - the input to resolveConnectedClusterCards(). */
export interface WorkloadClusterEntry {
  workloadId: string;
  rawClusters: PcsSizingEngineResponse.Cluster[];
  summaries: VcfSizingOutput.ClusterSummary[];
}

/** ConnectedWorkload.id is numeric while WorkloadRecommendation.workloadId is a string that may be
 *  zero-padded ("002"), so compare both textually and numerically. */
function isSameWorkloadId(connectedId: number | string, workloadId: string): boolean {
  return String(connectedId) === workloadId || (workloadId.trim() !== '' && Number(workloadId) === Number(connectedId));
}

export type ClusterDependencyScope = 'cluster' | 'workload';

/** Workload ids are domain-local; qualify membership when the caller supplies its domain. */
function hasWorkloadMembership(cluster: PcsSizingEngineResponse.Cluster, workloadId: string, domainId?: string): boolean {
  return cluster.connectedWorkloads?.some(w => isSameWorkloadId(w.id, workloadId)
    && (domainId === undefined || String(w.workloadDomainId) === String(domainId))) ?? false;
}

function hasScopedMembershipMetadata(cluster: PcsSizingEngineResponse.Cluster, domainId?: string): boolean {
  return cluster.connectedWorkloads?.some(w => domainId === undefined
    || (w.workloadDomainId != null && String(w.workloadDomainId).trim() !== '')) ?? false;
}

/** Resolve whole-cluster connections or workload-specific dependencies, excluding existing cards.
 * Domain-qualified membership prevents collisions between domain-local workload ids. */
export function resolveConnectedClusterCards(
  entries: WorkloadClusterEntry[],
  currentDomainId?: string,
  scope: ClusterDependencyScope = 'cluster',
): Map<string, VcfSizingOutput.ClusterSummary[]> {
  const summaryByClusterId = new Map<string, VcfSizingOutput.ClusterSummary>();
  const rawByClusterId = new Map<string, PcsSizingEngineResponse.Cluster>();
  const sourceWorkloadByClusterId = new Map<string, string>();
  entries.forEach(e => e.rawClusters.forEach((c, i) => {
    if (!c.clusterId) return;
    summaryByClusterId.set(c.clusterId, e.summaries[i]);
    rawByClusterId.set(c.clusterId, c);
    sourceWorkloadByClusterId.set(c.clusterId, e.workloadId);
  }));

  const adjacency = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    adjacency.get(a)!.add(b);
  };
  rawByClusterId.forEach((c, id) => c.connectedClusters?.forEach(({ clusterId: otherId }) => { link(id, otherId); link(otherId, id); }));

  const result = new Map<string, VcfSizingOutput.ClusterSummary[]>();
  entries.forEach(owner => {
    const ownedIds = new Set(owner.rawClusters.map(c => c.clusterId).filter((id): id is string => !!id));
    const visible = new Set<string>();
    if (scope === 'cluster') {
      ownedIds.forEach(id => adjacency.get(id)?.forEach(otherId => { if (!ownedIds.has(otherId)) visible.add(otherId); }));
    } else {
      // Only this workload's directional links are eligible as a legacy fallback. A sibling's
      // copy of a shared compute cluster may mount a different storage cluster.
      owner.rawClusters.forEach(c => c.connectedClusters?.forEach(({ clusterId }) => {
        const candidate = rawByClusterId.get(clusterId);
        if (!ownedIds.has(clusterId) && candidate && !hasScopedMembershipMetadata(candidate, currentDomainId)) visible.add(clusterId);
      }));
    }
    rawByClusterId.forEach((c, id) => {
      if (!ownedIds.has(id) && hasWorkloadMembership(c, owner.workloadId, currentDomainId)) visible.add(id);
    });
    // The engine mints clusterIds per call, so the owner's own cluster can come back from a sibling
    // workload under a different id (e.g. after a Recalculate) and slip past the id check above - the
    // same cluster name is the same cluster. The same goes for one connected cluster listed twice.
    // Splits of one connected cluster all come from one source workload and are all kept.
    const ownedNames = new Set(owner.rawClusters.map(c => clusterIdentityKey(c)).filter(Boolean));
    const sourceByName = new Map<string, string | undefined>();
    const connected = [...visible].filter(id => {
      const name = clusterIdentityKey(rawByClusterId.get(id) ?? {});
      if (!name) return true;
      if (ownedNames.has(name)) return false;
      const source = sourceWorkloadByClusterId.get(id);
      if (!sourceByName.has(name)) sourceByName.set(name, source);
      return sourceByName.get(name) === source;
    });
    result.set(owner.workloadId, connected.map(id => summaryByClusterId.get(id)!).filter(Boolean));
  });
  return result;
}

/** An alternate ("what-if") profile-tier candidate's Cluster can omit connectedClusters/
 *  connectedWorkloads - those are only reliably populated on the engine-recommended tier. Falls
 *  back to the recommended tier's own cluster of the same type so sharing-derived sections
 *  (Connected Clusters, Mounted Storage Clusters, Connected Workloads) don't disappear just
 *  because a non-default tier/candidate is what's currently being summarized. */
export function resolveClusterSharingSource(
  cluster: PcsSizingEngineResponse.Cluster,
  defaultClusters?: PcsSizingEngineResponse.Cluster[],
): PcsSizingEngineResponse.Cluster {
  return cluster.connectedClusters?.length || cluster.connectedWorkloads?.length
    ? cluster
    : defaultClusters?.find((dc) => dc.clusterType === cluster.clusterType) ?? cluster;
}

/** Derives the sharing-related ClusterSummary fields (Connected Workloads, Mounted Storage
 *  Clusters, Cross-WLD) from a single response Cluster - shared by both buildClusterSummaries()
 *  and WorkloadDomainComponent's own inline builder so the two stay in sync. */
export function buildClusterSharingFields(
  c: PcsSizingEngineResponse.Cluster,
  clusterRole: 'COMPUTE' | 'STORAGE' | undefined,
  context: ClusterSharingContext = {},
): Pick<VcfSizingOutput.ClusterSummary, 'connectedComputeClusterNames' | 'connectedWorkloadNames' | 'connectedWorkloads' | 'mountedStorageClusterNames' | 'isCrossWorkloadDomainCluster'> {
  const { currentDomainId } = context;
  const label = (name: string, domainId: number, domain: string) =>
    currentDomainId === undefined || String(domainId) === currentDomainId ? name : `${name} / ${domain}`;
  // De-duplicated: a stretched peer is listed once per site, but it is one cluster to the user.
  const clusterLabels = c.connectedClusters
    ? [...new Set(c.connectedClusters.map(cc => label(cc.clusterName, cc.workloadDomainId, cc.workloadDomainName)))]
    : undefined;
  const connectedWorkloads = buildConnectedWorkloadSummaries(c.connectedWorkloads, context);
  return {
    connectedWorkloads,
    connectedComputeClusterNames: clusterRole === 'STORAGE' && clusterLabels?.length ? clusterLabels : undefined,
    connectedWorkloadNames: connectedWorkloads?.map(w => w.displayName),
    // An HCI cluster has no role, but one reused as a dHCI compute cluster lists the SON it mounts in
    // connectedClusters just like a CON does - so it gets the same Mounted Storage Clusters badge.
    mountedStorageClusterNames: (clusterRole === 'COMPUTE' || (!clusterRole && c.clusterType === 'HCI')) && clusterLabels?.length ? clusterLabels : undefined,
    isCrossWorkloadDomainCluster: c.serverConfiguration?.chassis?.owner === false,
  };
}

/** Ported as-is from WorkloadDomainComponent.calculateNoOfdrives (workload-domain.component.ts:1493-1495). */
function sumDrives(driveDistribution: { numberOfDrives?: number }[]): number {
  return driveDistribution.reduce((a, b) => a + (b.numberOfDrives ?? 0), 0);
}

/**
 * Ported as-is from WorkloadDomainComponent.buildClusterSummariesForCarousel
 * (workload-domain.component.ts:337-384) - builds the per-cluster cards shown in the Cluster Card
 * Carousel, directly from the engine-response `siteClusters` (not from the frozen `summary`, whose
 * VcfSizingOutput.Workload.clusters is populated entirely server-side and never carries the vSAN
 * OSA fields below). For a stretched workload only the Primary site's clusters are shown (Secondary
 * mirrors Primary). Each card is flagged `isStretched` so ClusterCardComponent can show the "x2"
 * indicator, since the values on a stretched card are per-site, not domain-wide.
 */
export function buildCarouselClusterSummaries(
  siteClusters: PcsSizingEngineResponse.Cluster[] | undefined,
  sharingContext: ClusterSharingContext = {},
  defaultClusters?: PcsSizingEngineResponse.Cluster[],
): VcfSizingOutput.ClusterSummary[] {
  if (!siteClusters) return [];
  const isStretched = isStretchedByRecommendation(siteClusters);
  return filterPrimarySiteClusters(siteClusters, isStretched).map((cluster, index) =>
    // An unsited cluster (e.g. the CON/HCI side when only the storage cluster is stretched) is single-site: no "x2".
    buildCarouselClusterSummary(cluster, index, isStretched && !!cluster.siteType, sharingContext, defaultClusters),
  );
}

function buildCarouselClusterSummary(
  cluster: PcsSizingEngineResponse.Cluster,
  index: number,
  isStretched: boolean,
  sharingContext: ClusterSharingContext,
  defaultClusters?: PcsSizingEngineResponse.Cluster[],
): VcfSizingOutput.ClusterSummary {
  const sharingSource = resolveClusterSharingSource(cluster, defaultClusters);
  const server = cluster.serverConfiguration;
  const hosts = parseFloat(cluster.clusterConfiguration.nodesSize.value) || 0;
  const capacitySpec = cluster.diskGroupConfiguration?.capacitySpecification || cluster.diskConfiguration?.capacitySpecification;
  // capacityDrive.value is the authoritative per-host drive count (matches buildClusterSummaries()
  // above) - driveDistribution is OSA-specific and can be empty/all-zero (e.g. for ESA clusters).
  const capacityDrivesPerHost = server.capacityDrive?.value ?? (capacitySpec ? sumDrives(capacitySpec.driveDistribution) : 0);
  const driveSizeSpec = capacitySpec?.selectedCapacityDriveSize;
  const clusterRole = clusterRoleFromClusterType(cluster.clusterType);
  const name = buildClusterDisplayName(cluster);

  const summary: VcfSizingOutput.ClusterSummary = {
    id: cluster.clusterId || `cluster-${index}`,
    index,
    name,
    clusterId: cluster.clusterId ?? undefined,
    hosts,
    vms: parseInt(cluster.clusterConfiguration.vmsSize.value) || 0,
    cores: (server.cores?.value || 0) * hosts,
    memory: { value: (parseFloat(server.memory.value) || 0) * hosts, unit: server.memory.units || 'GB' },
    rawStorage: { value: cluster.clusterType === 'CON' ? 0 : parseFloat(cluster.clusterConfiguration.rawCapacity?.value?.toString() || '0'), unit: cluster.clusterConfiguration.rawCapacity?.units || 'TB' },
    memoryUsedByAppsPerHost: { value: parseFloat(server.requiredMemory?.value || '0'), unit: server.requiredMemory?.units || 'GB' },
    capacityDrivesPerHost,
    driveSize: { value: parseFloat(driveSizeSpec?.value || '0'), unit: driveSizeSpec?.units || 'GB' },
    vsanArchitecture: cluster.vsanArchitecture,
    clusterType: cluster.clusterType,
    clusterBaseName: cluster.clusterName ?? undefined,
    isStretched,
    clusterRole,
    ...buildClusterSharingFields(sharingSource, clusterRole, sharingContext),
  };

  if (server.diskGroups) {
    summary.diskGroupsCount = parseFloat(server.diskGroups.value) || 0;
  }
  const cacheDriveSize = cluster.diskGroupConfiguration?.cacheSpecification?.selectedCacheDriveSize;
  if (cacheDriveSize) {
    summary.cacheTierDriveSize = { value: parseFloat(cacheDriveSize.value) || 0, unit: cacheDriveSize.units || 'GB' };
  }

  return summary;
}

/**
 * Witness appliances grouped by type, sorted by type. Moved from WorkloadDomainComponent /
 * ManagementDomainComponent.getWitnessAppliancesSummary (identical copies) so the Witness tab and
 * the Witness cluster card share one implementation.
 */
export function summarizeWitnessAppliances(witnessClusters: PcsSizingEngineResponse.Cluster[] | undefined): { applianceType: string; count: number }[] {
  if (!witnessClusters || witnessClusters.length === 0) return [];
  const countMap = new Map<string, number>();
  witnessClusters.forEach((cluster) => {
    if (cluster.additionalInfo && 'applianceType' in cluster.additionalInfo) {
      const type = cluster.additionalInfo.applianceType;
      if (type) {
        countMap.set(type, (countMap.get(type) || 0) + 1);
      }
    }
  });
  return Array.from(countMap.entries())
    .map(([applianceType, count]) => ({ applianceType, count }))
    .sort((a, b) => a.applianceType.localeCompare(b.applianceType));
}

/** Version-specific "deploy a vSAN witness appliance" doc link (moved from the two output components). */
export function getWitnessDocumentationUrl(version: string | undefined): string {
  if ((version || '').startsWith('9.')) {
    return 'https://techdocs.broadcom.com/us/en/vmware-cis/vcf/vcf-9-0-and-later/9-0/vsan-deployment-administration-and-monitoring/vsan-planning-and-deployment/working-with-virtual-san-stretched-cluster/deploying-a-witness-appliance.html';
  }
  return 'https://techdocs.broadcom.com/us/en/vmware-cis/vcf/vcf-5-2-and-earlier/5-2/map-for-administering-vcf-5-2/stretching-clusters-admin/deploy-vsan-witness-host-admin.html';
}

/**
 * Per-site variant of buildCarouselClusterSummaries for a cluster's OWN page: a stretched workload
 * gets one card per Primary site cluster, one per Secondary site cluster (each with its own real
 * values, not a mirror of Primary) and one Witness card per cluster key (witness clusters carry no
 * clusterConfiguration, only an appliance type). Every card carries `siteType`, so none shows the
 * "x2" indicator. Secondary/Witness ids are suffixed so `track cluster.id` stays unique while
 * `clusterId`/`clusterBaseName` still match the Primary card's tree leaf. A non-stretched
 * workload returns exactly buildCarouselClusterSummaries()'s output (no siteType).
 */
export function buildSiteClusterSummaries(
  siteClusters: PcsSizingEngineResponse.Cluster[] | undefined,
  sharingContext: ClusterSharingContext = {},
  defaultClusters?: PcsSizingEngineResponse.Cluster[],
): VcfSizingOutput.ClusterSummary[] {
  if (!siteClusters) return [];
  if (!isStretchedByRecommendation(siteClusters)) {
    return buildCarouselClusterSummaries(siteClusters, sharingContext, defaultClusters);
  }
  const forSite = (siteType: 'PRIMARY' | 'SECONDARY') =>
    siteClusters
      .filter((c) => c.siteType === siteType)
      .map((cluster, index) => {
        const card = buildCarouselClusterSummary(cluster, index, true, sharingContext, defaultClusters);
        return { ...card, siteType, id: siteType === 'PRIMARY' ? card.id : `${card.id}::${siteType}` };
      });

  const witnessGroups = new Map<string, PcsSizingEngineResponse.Cluster[]>();
  siteClusters
    .filter((c) => c.siteType === 'WITNESS' && c.clusterType !== 'CON')
    .forEach((c) => {
      const key = c.clusterId || c.clusterName || '';
      witnessGroups.set(key, [...(witnessGroups.get(key) ?? []), c]);
    });
  const witnessCards = Array.from(witnessGroups.entries()).map(([key, clusters], index): VcfSizingOutput.ClusterSummary => {
    const first = clusters[0];
    return {
      id: `${key || `cluster-${index}`}::WITNESS`,
      index,
      name: buildClusterDisplayName(first),
      clusterId: first.clusterId ?? undefined,
      hosts: 0,
      vms: 0,
      cores: 0,
      memory: { value: 0, unit: 'GB' },
      rawStorage: { value: 0, unit: 'TB' },
      memoryUsedByAppsPerHost: { value: 0, unit: 'GB' },
      capacityDrivesPerHost: 0,
      driveSize: { value: 0, unit: 'GB' },
      clusterType: first.clusterType,
      clusterBaseName: first.clusterName ?? undefined,
      isStretched: true,
      siteType: 'WITNESS',
      witnessAppliances: summarizeWitnessAppliances(clusters),
    };
  });

  // Clusters with no siteType (the CON/HCI side when only the storage cluster is stretched) are
  // single-site: they get a plain card (no siteType, no "x2") rather than being dropped.
  const unsited = siteClusters
    .filter((c) => !c.siteType)
    .map((cluster, index) => buildCarouselClusterSummary(cluster, index, false, sharingContext, defaultClusters));

  return [...unsited, ...forSite('PRIMARY'), ...forSite('SECONDARY'), ...witnessCards];
}

/**
 * Moves WorkloadDomainComponent.rebuildGroupedComponentsCache's per-workload carousel-cache loop
 * + mergeConnectedClusterCards (workload-domain.component.ts:1269-1346) into one pure function:
 * builds each workload's own cluster cards via buildCarouselClusterSummaries(), then appends every
 * workload's bi-directionally connected cards (resolveConnectedClusterCards()) onto its own entry.
 * `resolveEffective` is injected rather than imported from the adapters layer - the only caller
 * with unsaved-preview state (WorkloadDomainComponent) needs to overlay its own
 * pendingReadyNodeSaveMap first; a caller with no such state just passes the plain
 * getEffectiveWorkloadRecommendation.
 *
 * `defaultClusters` (the connectedClusters/connectedWorkloads sharing-metadata source for a card
 * whose own cluster lacks it - see resolveClusterSharingSource) is just `profileRecommendations`'s
 * one recommended entry's `.clusters` for a non-mixed (CON-only or SON/HCI-only) response. For a
 * MIXED dHCI response, `.find(isRecommended)` searching the WHOLE array would always land on the
 * SON/HCI side's own recommended tier (CON's lone entry is rarely/never isRecommended - see
 * partitionProfileRecommendationsBySide's own doc comment), so a CON card's sharing-source lookup
 * would search for a 'CON'-typed entry inside SON's tier clusters and never find one - the real
 * cause of a customized CON cluster's Connected Clusters/Connected Workloads/Mounted Storage
 * Clusters badges never appearing. Built per-side instead: CON's own slice comes from the raw
 * top-level `recomm.clusters` (the real, currently-recommended sizing - always populated with
 * sharing metadata, unlike the separate CON profileRecommendations "estimate" entry), concatenated
 * with the SON/HCI side's own recommended tier exactly as before.
 */
export function buildWorkloadClusterCardMap(
  recommendation: PcsSizingEngineResponse.Recommendation | undefined,
  sharingContext: ClusterSharingContext,
  resolveEffective: (r: PcsSizingEngineResponse.WorkloadRecommendation) => PcsSizingEngineResponse.WorkloadRecommendation,
  perSite = false,
  dependencyScope: ClusterDependencyScope = 'cluster',
): Map<string, VcfSizingOutput.ClusterSummary[]> {
  const cache = new Map<string, VcfSizingOutput.ClusterSummary[]>();
  const entries: WorkloadClusterEntry[] = [];
  // Resolve once: profiles and pending previews must use the same effective records throughout.
  const effectiveByWorkload = new Map((recommendation?.workloadRecommendations ?? [])
    .filter(r => !!r.workloadId)
    .map(r => [r.workloadId, resolveEffective(r)]));
  const candidates = (recommendation?.workloadRecommendations ?? []).flatMap(r => [
    ...(r.clusters ?? []),
    ...(r.profileRecommendations?.filter(p => p.isRecommended).flatMap(p => p.clusters ?? []) ?? []),
    ...(effectiveByWorkload.get(r.workloadId)?.clusters ?? []),
  ]);
  const withSharingMetadata = (clusters: PcsSizingEngineResponse.Cluster[] | undefined, defaults: PcsSizingEngineResponse.Cluster[] | undefined) =>
    dependencyScope !== 'workload' ? clusters : clusters?.map(c => {
      const source = defaults?.find(d => d.clusterId === c.clusterId)
        ?? defaults?.find(d => d.clusterType === c.clusterType && d.clusterName === c.clusterName);
      return source ? { ...c,
        connectedWorkloads: c.connectedWorkloads?.length ? c.connectedWorkloads : source.connectedWorkloads,
        connectedClusters: c.connectedClusters?.length ? c.connectedClusters : source.connectedClusters,
      } : c;
    });
  const domainId = sharingContext.currentDomainId ?? recommendation?.requirementsId;
  const scopeStorageLinks = (clusters: PcsSizingEngineResponse.Cluster[] | undefined, workloadId: string) =>
    dependencyScope !== 'workload' ? clusters : clusters?.map(c => {
      if (c.clusterType === 'SON') return c;
      return { ...c, connectedClusters: c.connectedClusters?.filter(link => {
        const target = candidates.find(candidate => candidate.clusterId === link.clusterId);
        return !target || !hasScopedMembershipMetadata(target, domainId) || hasWorkloadMembership(target, workloadId, domainId);
      }) };
    });
  recommendation?.workloadRecommendations?.forEach(recomm => {
    if (!recomm.workloadId) return;
    const { conSide, otherSide } = partitionProfileRecommendationsBySide(recomm);
    const defaultClusters = conSide.length && otherSide.length
      ? [...(recomm.clusters?.filter((c) => c.clusterType === 'CON') ?? []), ...(otherSide.find((p) => p.isRecommended)?.clusters ?? [])]
      : recomm.profileRecommendations?.find((p) => p.isRecommended)?.clusters ?? recomm.clusters;
    const effectiveClusters = scopeStorageLinks(
      withSharingMetadata(effectiveByWorkload.get(recomm.workloadId)?.clusters, defaultClusters), recomm.workloadId);
    const cards = buildCarouselClusterSummaries(effectiveClusters, sharingContext, scopeStorageLinks(defaultClusters, recomm.workloadId));
    // perSite: a cluster's own page shows Primary/Secondary/Witness cards; every other consumer keeps
    // the Primary-only cards. `entries` (connected-card resolution) always uses the Primary-only set.
    cache.set(recomm.workloadId, perSite ? buildSiteClusterSummaries(effectiveClusters, sharingContext, scopeStorageLinks(defaultClusters, recomm.workloadId)) : cards);
    entries.push({
      workloadId: recomm.workloadId,
      rawClusters: filterPrimarySiteClusters(effectiveClusters ?? [], isStretchedByRecommendation(effectiveClusters)),
      summaries: cards,
    });
  });

  // Same attach-to-target-cluster step RecommendationSummaryBuilderService applies to
  // `summary.workloadDomains[]` - this function is the one other place ClusterSummary[] gets built
  // straight from the raw recommendation (WorkloadDomainComponent's Cluster Summary rail,
  // WorkloadClustersViewComponent's Workload View cards), so it needs its own copy of the step.
  // Resolved from `entries` (this domain's own clusters only) BEFORE the connected-cards merge
  // below, so a cross-domain connected card is never mistaken for the target.
  const domainComponents = recommendation?.components?.filter(c => c.enabled && c.placementType === 'WORKLOAD');
  if (domainComponents?.length) {
    const targetId = resolveComponentsTargetClusterId(entries.map(e => ({ clusters: e.summaries }) as VcfSizingOutput.Workload));
    const targetCluster = entries.flatMap(e => e.summaries).find(c => c.id === targetId);
    if (targetCluster) {
      targetCluster.hostedDomainComponents = domainComponents;
    }
    // perSite: the cache holds its own freshly built card objects, not `entries`' - the target has to
    // be stamped on those too or the Cluster Summary rail never sees it. A stretched cluster's
    // Secondary/Witness cards carry a suffixed id, so only the Primary card matches.
    const targetWorkloadId = entries.find(e => targetCluster && e.summaries.includes(targetCluster))?.workloadId;
    if (perSite && targetId && targetWorkloadId) {
      cache.get(targetWorkloadId)?.forEach(card => {
        if (card.id === targetId) {
          card.hostedDomainComponents = domainComponents;
        }
      });
    }
  }

  resolveConnectedClusterCards(entries, domainId, dependencyScope).forEach((connected, workloadId) => {
    if (connected.length) {
      cache.set(workloadId, [...(cache.get(workloadId) ?? []), ...connected]);
    }
  });
  return cache;
}
